import React, { FC, useContext, useMemo } from 'react';
import { Button } from 'ncoded-component-library';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import useMatchMedia from 'hooks/useMatchMedia';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { getSearchSectionTitle } from '../../utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import OverflowMenu from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/components/OverflowMenu/OverflowMenu.component';
import ClearFilters from 'components/ClearFilters';

import './SearchFilters.styles.scss';
import './SearchFilters.styles.responsive.scss';

type SearchFiltersProps = {
  className?: string;
  filters: Record<string, number>;
  activeFilter?: string;
  setFilter?: (value: string) => void;
};

const SearchFilters: FC<SearchFiltersProps> = ({
  className,
  filters,
  activeFilter,
  setFilter,
}) => {
  const { t } = useTranslation();
  const isPhablet = useMatchMedia('isPhablet');
  const classes = classNames('search-filter', className);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);

  const currActiveFilter = useMemo(
    () => Object.entries(filters).find(([filter]) => filter === activeFilter),
    [activeFilter, filters],
  );

  const filtersContent = useMemo(
    () => (
      <div className="search-filter__wrapper">
        {Object.entries(filters)
          .filter(([filter]) => !isPhablet || filter !== activeFilter)
          .map(([filter, value]) => (
            <Button
              key={filter}
              className={classNames({ active: filter === activeFilter })}
              disabled={!value}
              onClick={() => {
                dropdown?.setIsOpen(false);
                setFilter(filter);
              }}
            >
              {t(getSearchSectionTitle(filter, isSuperAdmin))}
              {` (${value})`}
            </Button>
          ))}
      </div>
    ),
    [activeFilter, dropdown, filters, isPhablet, isSuperAdmin, setFilter, t],
  );

  return (
    <div className={classes}>
      {isPhablet ? (
        <OverflowMenu
          ref={dropdownRef}
          title={`${t(
            getSearchSectionTitle(currActiveFilter[0], isSuperAdmin),
          )} (${currActiveFilter[1]})`}
        >
          {filtersContent}
        </OverflowMenu>
      ) : (
        filtersContent
      )}

      <ClearFilters text={t('clearSearch')} />
    </div>
  );
};

export default SearchFilters;
